import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from '@app/hooks';
import {
  ptoRdImportApi,
  useApplyPtoRdImportMutation,
  useGetPtoProjectQuery,
  useListPtoProjectsQuery,
  useUpdatePtoRdImportPlanMutation,
  type PtoRdImportPlan,
  type PtoRdPlanComplect,
  type PtoRdPlanTitul,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Table } from '@shared/ui/Table';
import { errorText, httpStatus } from './errors';
import { formatSize, plural } from './format';
import { MoreList } from './NoteList';
import { PROJECTS_LIMIT, ProjectPicker, projectLabel } from './ProjectPicker';
import {
  Cell,
  CheckLabel,
  Code,
  Details,
  ErrorText,
  List,
  Muted,
  NarrowInput,
  Panel,
  Row,
  Section,
  SectionTitle,
  Select,
  Small,
  Spread,
  Stack,
  StickyBar,
  SubCell,
  TargetBox,
  TitulCard,
  TitulHead,
  Tone,
  WideInput,
} from './styles';

/** Листы переноса: до стольких — списком, больше — свёрнуто. */
const CARRY_INLINE = 5;
/** Начало пометки комплекта о наименовании со скана, исправленном ИИ (спека, §3.3):
 *  «Наименование со скана исправлено ИИ: было «<текст OCR>» — проверьте». */
const AI_FIX_PREFIX = 'Наименование со скана исправлено ИИ';

const titulName = (t: PtoRdPlanTitul) => t.titul ?? t.object_code ?? t.key;

function StateBadge({ c }: { c: PtoRdPlanComplect }) {
  switch (c.state) {
    case 'same':
      return <Tone $tone="muted">уже загружено</Tone>;
    case 'older':
      return (
        <Stack>
          <Tone $tone="bad">в реестре новее: {c.active_rev_label ?? '—'}</Tone>
          <Small>ревизия в пакете старше — по умолчанию не загружается</Small>
        </Stack>
      );
    case 'update':
      return (
        <Stack>
          <Tone $tone="warn">новая ревизия</Tone>
          <Small>сейчас: {c.active_rev_label ?? '—'}</Small>
        </Stack>
      );
    default:
      return <Tone $tone="ok">новый</Tone>;
  }
}

/**
 * Текст скана до исправления ИИ из пометки комплекта: между первой открывающей
 * кавычкой после префикса и последней закрывающей (в самом тексте бывают
 * кавычки). Другая пометка или разобрать не удалось — `null`.
 */
function aiFixOriginal(warning: string): string | null {
  const w = warning.trim();
  if (!w.startsWith(AI_FIX_PREFIX)) return null;
  const open = w.indexOf('«', AI_FIX_PREFIX.length);
  const close = w.lastIndexOf('»');
  if (open < 0 || close <= open) return null;
  return w.slice(open + 1, close).trim() || null;
}

/**
 * Пометка под наименованием комплекта. Наименование со скана, исправленное ИИ, —
 * значок «исправлено ИИ» и текст скана до исправления (полная пометка — во
 * всплывающей подсказке); остальные пометки — как есть.
 */
function ComplectWarning({ warning }: { warning: string }) {
  const original = aiFixOriginal(warning);
  if (original === null) return <>{warning}</>;
  return (
    <>
      <Tone $tone="warn" title={warning}>
        исправлено ИИ
      </Tone>{' '}
      <Small>было: «{original}»</Small>
    </>
  );
}

/** Листы прежней ревизии, которых нет в пакете: переносить ли их в новую ревизию. */
function CarryOver({
  c,
  disabled,
  onToggle,
}: {
  c: PtoRdPlanComplect;
  disabled: boolean;
  onToggle: (on: boolean) => void;
}) {
  const carry = c.carry ?? [];
  const list = (
    <List>
      {carry.map((f, i) => (
        <li key={`${i}:${f}`}>
          <Code>{f}</Code>
        </li>
      ))}
    </List>
  );
  return (
    <>
      <CheckLabel>
        <input
          type="checkbox"
          checked={Boolean(c.carry_over)}
          disabled={disabled}
          aria-label={`Перенести листы прежней ревизии комплекта ${c.code} (${carry.length})`}
          onChange={(e) => onToggle(e.target.checked)}
        />
        Перенести листы прежней ревизии ({carry.length})
      </CheckLabel>{' '}
      <Small>
        — их нет в пакете{c.active_rev_label ? ` (ревизия ${c.active_rev_label})` : ''}; без
        переноса новая ревизия будет только из файлов пакета.
      </Small>
      {carry.length <= CARRY_INLINE ? (
        list
      ) : (
        <Details>
          <summary>Показать листы ({carry.length})</summary>
          {list}
        </Details>
      )}
    </>
  );
}

/**
 * Проверка раскладки пакета РД в одном закреплённом проекте: титулы,
 * комплекты (шифр, наименование, ревизия, грузить ли,
 * перенос листов прежней ревизии), файлы вне раскладки. Правки сохраняются на
 * сервере — он заново сверяет комплекты с реестром (ключи комплектов могут
 * измениться, поэтому черновик заменяется раскладкой из ответа); «Загрузить в
 * ОСНОВУ» ставит применение в очередь. `canWrite=false` — только просмотр.
 */
export function PlanReview({
  importId,
  plan,
  canWrite,
}: {
  importId: string;
  plan: PtoRdImportPlan;
  canWrite: boolean;
}) {
  const [draft, setDraft] = useState<PtoRdImportPlan>(plan);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Черновик берёт раскладку сервера, только когда приходит НОВЫЙ объект
  // раскладки (карточку перечитали) и несохранённых правок нет. Сравнивать с
  // `plan` из пропсов при каждом изменении `dirty` нельзя: сразу после
  // «Сохранить» `plan` ещё старый — черновик откатился бы к нему.
  const serverPlan = useRef(plan);
  const incomingPlan = useRef(plan);
  useEffect(() => {
    if (plan === incomingPlan.current) return;
    incomingPlan.current = plan;
    serverPlan.current = plan;
    if (!dirty) setDraft(plan);
  }, [plan, dirty]);
  const boundProjectId = serverPlan.current.project_id ?? null;
  const { data: projectsData, isFetching: projectsLoading } = useListPtoProjectsQuery(
    { limit: PROJECTS_LIMIT },
    { skip: !canWrite || Boolean(boundProjectId) },
  );
  const projects = projectsData?.items ?? [];
  const { currentData: projectData } = useGetPtoProjectQuery(draft.project_id ?? '', {
    skip: !draft.project_id,
  });
  const targetName = projectData
    ? projectLabel(projectData.project)
    : draft.tituls.find((t) => t.project_id === draft.project_id)?.project_name;
  const dispatch = useAppDispatch();
  const [save, { isLoading: saving }] = useUpdatePtoRdImportPlanMutation();
  const [apply, { isLoading: applying }] = useApplyPtoRdImportMutation();
  const locked = !canWrite || saving || applying;
  // У старого пакета сначала сохраняем цель; сверка с выбранным проектом
  // завершится до ручных изменений состава загрузки.
  const contentLocked = locked || !boundProjectId;
  /** 409 — пакет уже не на проверке (его применили или распознают заново): перечитать карточку. */
  const refreshOnConflict = (e: unknown) => {
    if (httpStatus(e) === 409) {
      dispatch(ptoRdImportApi.util.invalidateTags([{ type: 'PtoRdImport', id: importId }]));
    }
  };

  const edit = (next: (d: PtoRdImportPlan) => PtoRdImportPlan) => {
    setDraft(next);
    setDirty(true);
    setSaved(false);
  };
  const pickProject = (projectId: string | null) => {
    if (boundProjectId) return;
    const project = projects.find((p) => p.id === projectId);
    edit((d) => ({
      ...d,
      project_id: projectId,
      tituls: d.tituls.map((t) => ({
        ...t,
        project_id: projectId,
        project_name: project?.name ?? null,
        matched_by: null,
        suggested_project_id: null,
        suggested_project_name: null,
      })),
      complects: d.complects.map((c) => {
        const oldTarget = d.tituls.find((t) => t.key === c.titul_key)?.project_id;
        if (!projectId || oldTarget === projectId) return c;
        // Старые совпадения относятся к другому проекту. Снимаем только
        // автоматический пропуск «уже загружено» у целых файлов; другие
        // исключения инженер проверяет сам. Сервер заново сверит выбранный проект.
        const restore =
          c.state === 'same' &&
          c.files.length > 0 &&
          c.files.every((f) => f.size > 0 && /^[a-f\d]{64}$/i.test(f.sha256)) &&
          !c.warnings.some((w) => /ошиб|поврежд|не удалось|битый|битые/i.test(w));
        return {
          ...c,
          document_id: null,
          active_rev_label: null,
          state: 'new',
          carry: [],
          carry_over: false,
          skip: restore ? false : c.skip,
        };
      }),
    }));
  };
  const patchTitul = (key: string, patch: Partial<PtoRdPlanTitul>) =>
    edit((d) => ({
      ...d,
      tituls: d.tituls.map((t) => (t.key === key ? { ...t, ...patch } : t)),
    }));
  const patchComplect = (key: string, patch: Partial<PtoRdPlanComplect>) =>
    edit((d) => ({
      ...d,
      complects: d.complects.map((c) => (c.key === key ? { ...c, ...patch } : c)),
    }));
  const assignLoose = (fileId: string, to: string | null) =>
    edit((d) => ({
      ...d,
      loose: d.loose.map((l) => (l.file.id === fileId ? { ...l, assign_to: to } : l)),
    }));

  const totals = useMemo(() => {
    const skippedTitul = new Set(draft.tituls.filter((t) => t.skip).map((t) => t.key));
    const load = draft.complects.filter((c) => !c.skip && !skippedTitul.has(c.titul_key));
    const targets = new Set(load.map((c) => c.titul_key));
    return {
      load: load.length,
      bytes: load.reduce((s, c) => s + (c.size ?? 0), 0),
      targets,
      objects: new Set(draft.tituls.map((t) => t.object_code ?? '')).size,
      same: draft.complects.filter((c) => c.state === 'same').length,
      older: draft.complects.filter((c) => c.state === 'older').length,
    };
  }, [draft]);

  async function onSave(): Promise<boolean> {
    setError(null);
    if (!draft.project_id) {
      setError('Выберите проект для всего пакета РД.');
      return false;
    }
    // Грузится комплект — грузится и его титул (он в `targets`); пустой шифр у
    // пропущенного комплекта сервер просто не примет и оставит прежний.
    const blankCode = draft.complects.find(
      (c) => !c.skip && totals.targets.has(c.titul_key) && !c.code.trim(),
    );
    if (blankCode) {
      setError('У комплекта не указан шифр — заполните его или снимите галочку «Грузить».');
      return false;
    }
    try {
      const r = await save({ id: importId, plan: draft }).unwrap();
      // Сервер пересчитал ключи и состояния комплектов — берём его раскладку.
      setDraft(r.plan);
      serverPlan.current = r.plan;
      setDirty(false);
      setSaved(true);
      return true;
    } catch (e) {
      setError(errorText(e, 'Не удалось сохранить правки.'));
      refreshOnConflict(e);
      return false;
    }
  }

  /** Отменить несохранённые правки — вернуть раскладку сервера. */
  function onDiscard() {
    setDraft(serverPlan.current);
    setDirty(false);
    setError(null);
  }

  async function onApply() {
    if (!boundProjectId || !draft.project_id) return;
    if (dirty && !(await onSave())) return;
    setError(null);
    try {
      await apply(importId).unwrap();
    } catch (e) {
      setError(errorText(e, 'Не удалось поставить загрузку в очередь.'));
      refreshOnConflict(e);
    }
  }

  const titulOf = new Map(draft.tituls.map((t) => [t.key, t]));

  return (
    <div>
      <Panel>
        <TargetBox>
          <strong>Проект для всего пакета</strong>
          {boundProjectId ? (
            <>
              <div>{targetName ?? 'Проект пакета'}</div>
              <Small>Все титулы и комплекты загружаются только в этот проект.</Small>
            </>
          ) : canWrite ? (
            <>
              <ProjectPicker
                projectId={draft.project_id ?? null}
                projects={projects}
                loading={projectsLoading}
                locked={locked}
                onPick={pickProject}
              />
              <Small>Выберите проект и сохраните привязку, затем проверьте комплекты.</Small>
            </>
          ) : (
            <Small>Проект пакета ещё не выбран.</Small>
          )}
        </TargetBox>
        <Muted>
          Найдено: объектов — {totals.objects}, титулов — {draft.tituls.length}, комплектов —{' '}
          {draft.complects.length}
          {totals.same > 0 ? `, из них уже загружено — ${totals.same}` : ''}
          {totals.older > 0 ? `, в реестре новее — ${totals.older}` : ''}
          {draft.loose.length > 0 ? `; вне раскладки файлов — ${draft.loose.length}` : ''}.
          {canWrite
            ? ' Проверьте титулы, шифры и наименования комплектов: наименования взяты из основной надписи, шифр и ревизия — из имени файла.'
            : ''}
        </Muted>
      </Panel>

      {draft.tituls.map((t) => {
        const complects = draft.complects.filter((c) => c.titul_key === t.key);
        const toLoad = complects.filter((c) => !c.skip && !t.skip);
        const name = titulName(t);
        return (
          <TitulCard key={t.key}>
            <TitulHead>
              <div>
                <strong>Титул {t.titul ?? '—'}</strong>
                {t.building ? <> · {t.building}</> : null}
                <div>
                  <Small>
                    {[t.object_code, t.object_name].filter(Boolean).join(' · ') ||
                      'объект не указан'}
                  </Small>
                </div>
                <div>
                  <Small>
                    комплектов: {complects.length}, к загрузке: {toLoad.length} (
                    {formatSize(toLoad.reduce((s, c) => s + (c.size ?? 0), 0))})
                  </Small>
                </div>
              </div>
              <TargetBox>
                <CheckLabel>
                  <input
                    type="checkbox"
                    checked={t.skip}
                    disabled={contentLocked}
                    aria-label={`Не загружать титул ${name}`}
                    onChange={(e) => patchTitul(t.key, { skip: e.target.checked })}
                  />
                  Не загружать титул
                </CheckLabel>
              </TargetBox>
            </TitulHead>
            <Table>
              <thead>
                <tr>
                  <th>Грузить</th>
                  <th>Марка</th>
                  <th>Шифр</th>
                  <th>Наименование</th>
                  <th>Ревизия</th>
                  <th>Листов</th>
                  <th>Состояние</th>
                </tr>
              </thead>
              <tbody>
                {complects.map((c) => {
                  const off = c.skip || t.skip;
                  return (
                    <Fragment key={c.key}>
                      <tr>
                        <Cell>
                          <input
                            type="checkbox"
                            aria-label={`Грузить комплект ${c.code}`}
                            checked={!off}
                            disabled={contentLocked || t.skip}
                            onChange={(e) => patchComplect(c.key, { skip: !e.target.checked })}
                          />
                        </Cell>
                        <Cell>{c.mark ?? '—'}</Cell>
                        <Cell>
                          <WideInput
                            value={c.code}
                            disabled={contentLocked}
                            aria-label={`Шифр комплекта ${c.code}`}
                            onChange={(e) => patchComplect(c.key, { code: e.target.value })}
                          />
                          <div>
                            <Small>
                              {c.files.length} {plural(c.files.length, ['файл', 'файла', 'файлов'])}
                              {c.size ? ` · ${formatSize(c.size)}` : ''}
                            </Small>
                          </div>
                        </Cell>
                        <Cell>
                          <WideInput
                            value={c.title}
                            disabled={contentLocked}
                            aria-label={`Наименование комплекта ${c.code}`}
                            onChange={(e) => patchComplect(c.key, { title: e.target.value })}
                          />
                          {c.discipline ? (
                            <div>
                              <Small>{c.discipline}</Small>
                            </div>
                          ) : null}
                          <MoreList
                            items={c.warnings}
                            limit={3}
                            itemKey={(w, i) => `${i}:${w}`}
                            render={(w) => <ComplectWarning warning={w} />}
                          />
                        </Cell>
                        <Cell>
                          <NarrowInput
                            value={c.rev_label ?? ''}
                            disabled={contentLocked}
                            aria-label={`Ревизия комплекта ${c.code}`}
                            onChange={(e) =>
                              patchComplect(c.key, { rev_label: e.target.value || null })
                            }
                          />
                        </Cell>
                        <Cell>{c.pages || '—'}</Cell>
                        <Cell>
                          <StateBadge c={c} />
                        </Cell>
                      </tr>
                      {(c.carry ?? []).length > 0 ? (
                        <tr>
                          <SubCell colSpan={7}>
                            <CarryOver
                              c={c}
                              disabled={contentLocked || off}
                              onToggle={(on) => patchComplect(c.key, { carry_over: on })}
                            />
                          </SubCell>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </Table>
          </TitulCard>
        );
      })}

      {draft.loose.length > 0 ? (
        <Section>
          <SectionTitle>Файлы вне раскладки</SectionTitle>
          <Panel>
            <Muted>
              По умолчанию не загружаются. Файл без шифра можно добавить к комплекту вручную — или
              переименуйте его по шифру и загрузите заново.
            </Muted>
            <Table>
              <thead>
                <tr>
                  <th>Файл</th>
                  <th>Почему</th>
                  <th>Добавить к комплекту</th>
                </tr>
              </thead>
              <tbody>
                {draft.loose.map((l) => (
                  <tr key={l.file.id}>
                    <Cell>
                      <Code>{l.file.path}</Code>
                      {l.file.size ? (
                        <div>
                          <Small>{formatSize(l.file.size)}</Small>
                        </div>
                      ) : null}
                    </Cell>
                    <Cell>{l.reason}</Cell>
                    <Cell>
                      <Select
                        value={l.assign_to ?? ''}
                        disabled={contentLocked}
                        aria-label={`Добавить файл ${l.file.filename} к комплекту`}
                        onChange={(e) => assignLoose(l.file.id, e.target.value || null)}
                      >
                        <option value="">— не загружать —</option>
                        {draft.tituls.map((t) => {
                          const own = draft.complects.filter((c) => c.titul_key === t.key);
                          return own.length > 0 ? (
                            <optgroup key={t.key} label={`Титул ${titulName(t)}`}>
                              {own.map((c) => (
                                <option key={c.key} value={c.key}>
                                  {c.code}
                                </option>
                              ))}
                            </optgroup>
                          ) : null;
                        })}
                        {draft.complects
                          .filter((c) => !titulOf.has(c.titul_key))
                          .map((c) => (
                            <option key={c.key} value={c.key}>
                              {c.code}
                            </option>
                          ))}
                      </Select>
                    </Cell>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Panel>
        </Section>
      ) : null}

      {canWrite ? (
        <StickyBar>
          <Spread>
            <Muted>
              К загрузке: комплектов — {totals.load} ({formatSize(totals.bytes)}).
              {dirty
                ? ' Есть несохранённые правки.'
                : saved
                  ? ' Правки сохранены, раскладка сверена с реестром.'
                  : ''}
            </Muted>
            <Row>
              {dirty ? (
                <Button $variant="ghost" disabled={locked} onClick={onDiscard}>
                  Отменить правки
                </Button>
              ) : null}
              <Button
                $variant="secondary"
                disabled={!dirty || locked || !draft.project_id}
                onClick={() => void onSave()}
              >
                {saving ? 'Сохранение…' : 'Сохранить правки'}
              </Button>
              <Button
                $variant="primary"
                disabled={totals.load === 0 || contentLocked || !draft.project_id}
                onClick={() => void onApply()}
              >
                {applying ? 'Ставим в очередь…' : 'Загрузить в ОСНОВУ'}
              </Button>
            </Row>
          </Spread>
          {error ? <ErrorText>{error}</ErrorText> : null}
        </StickyBar>
      ) : null}
    </div>
  );
}
